/*
 * Isola cada região de CROMO da página de conteúdo: se uma quebra, ela some e as outras — e o
 * artigo — continuam de pé.
 *
 * Existe separado do `Error/Boundary/Boundary` porque aquele é uma tela cheia ("Ops, algo deu
 * errado", com `height: window.innerHeight`): dentro de um bloco da coluna direita ele tomaria
 * a página por causa de um cartão de relacionado. Aqui o fallback é NADA, como no
 * `DisclosureErrorBoundary`.
 *
 * O que este componente NÃO envolve é o cabeçalho e o corpo do artigo: eles não são
 * renderizados pelo React, são nós adotados (`AdoptedRegion`), e ficar sob um boundary
 * significaria poder serem desmontados por um erro de outro bloco.
 */
import React from 'react';

import apm from '../../../../../services/APM';

declare global {
    interface Window {
        /** Publicada pelo partial `stale-assets-recovery.blade.php`; renavega a pagina 1x a cada 5min. */
        astChunkReloadOnce?: () => void;
    }
}

interface IChromeBoundaryState {
    hasError: boolean;
}

interface IChromeBoundaryProps {
    /** Nome da região, para o erro chegar identificado no APM. */
    region: string;
}

class ChromeBoundary extends React.Component<
    React.PropsWithChildren<IChromeBoundaryProps>,
    IChromeBoundaryState
> {
    constructor(props: React.PropsWithChildren<IChromeBoundaryProps>) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): IChromeBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        const { region } = this.props;

        apm.captureError(error);

        // a região não cabe no `captureError` do RUM, que só aceita o erro; vai no console
        // para o rastro local dizer QUAL bloco caiu
        console.error(`[ArticleTemplate] falha na região "${region}"`, error, errorInfo.componentStack);

        // Chunk stale apos deploy: o boundary captura o erro, entao o unhandledrejection
        // do partial chunk-load-recovery nunca o ve (React anexa handler de rejeicao ao import).
        if (
            /dynamically imported module|Importing a module script failed|Failed to fetch dynamically imported/i.test(
                error.message
            )
        ) {
            window.astChunkReloadOnce?.();
        }
    }

    render() {
        const { hasError } = this.state;
        const { children } = this.props;

        if (hasError) return null;

        return children;
    }
}

export default ChromeBoundary;
